'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { FiArrowUpRight } from 'react-icons/fi';
import { useLanguageStore } from '@/providers/useLanguageStore';

type YoutubeShort = {
  id: string;
  url: string;
  embedUrl: string;
};

const YOUTUBE_CHANNEL_URL =
  'https://www.youtube.com/playlist?list=PLa0MKW7UhcxVOyoqc8THmk6GnljGx1_WT';

const RollingCodeShortsSection: React.FC = () => {
  const { translations } = useLanguageStore();
  const [shorts, setShorts] = useState<YoutubeShort[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadedShortIds, setLoadedShortIds] = useState<Record<string, boolean>>({});
  const [thumbnailVariantById, setThumbnailVariantById] = useState<Record<string, number>>({});
  const pendingThumbnailCount = shorts.filter((short) => !loadedShortIds[short.id]).length;
  const isThumbnailLoading = !loading && shorts.length > 0 && pendingThumbnailCount > 0;

  const translateText = (key: string, fallback: string) => translations[key] || fallback;

  const loadingThumbnailsTextTemplate = translateText(
    'shortsLoadingThumbnails',
    'Cargando miniaturas ({pending}/{total})...'
  );

  const loadingThumbnailsText = loadingThumbnailsTextTemplate
    .replace('{pending}', String(pendingThumbnailCount))
    .replace('{total}', String(shorts.length));

  useEffect(() => {
    const controller = new AbortController();

    const loadShorts = async () => {
      try {
        const response = await fetch('/api/youtube/shorts?limit=4', {
          signal: controller.signal,
        });

        if (!response.ok) {
          setShorts([]);
          return;
        }

        const data = (await response.json()) as { shorts?: YoutubeShort[] };
        setShorts(Array.isArray(data.shorts) ? data.shorts : []);
      } catch {
        setShorts([]);
      } finally {
        setLoading(false);
      }
    };

    loadShorts();

    return () => controller.abort();
  }, []);

  useEffect(() => {
    const nextLoadedMap = shorts.reduce<Record<string, boolean>>((acc, short) => {
      acc[short.id] = false;
      return acc;
    }, {});

    const nextThumbnailVariantMap = shorts.reduce<Record<string, number>>((acc, short) => {
      acc[short.id] = 0;
      return acc;
    }, {});

    setLoadedShortIds(nextLoadedMap);
    setThumbnailVariantById(nextThumbnailVariantMap);
  }, [shorts]);

  const markShortAsLoaded = (shortId: string) => {
    setLoadedShortIds((current) => {
      if (current[shortId]) {
        return current;
      }

      return {
        ...current,
        [shortId]: true,
      };
    });
  };

  const getThumbnailSrc = (shortId: string, variant: number) => {
    if (variant === 0) {
      return `https://i.ytimg.com/vi/${shortId}/oardefault.jpg`;
    }

    if (variant === 1) {
      return `https://i.ytimg.com/vi/${shortId}/hqdefault.jpg`;
    }

    return `https://i.ytimg.com/vi/${shortId}/mqdefault.jpg`;
  };

  const handleThumbnailError = (shortId: string) => {
    setThumbnailVariantById((current) => {
      const currentVariant = current[shortId] ?? 0;
      const nextVariant = Math.min(currentVariant + 1, 2);

      if (nextVariant === currentVariant) {
        return current;
      }

      return {
        ...current,
        [shortId]: nextVariant,
      };
    });

    markShortAsLoaded(shortId);
  };

  return (
    <section className="mt-8 rounded-2xl border border-red-100 bg-white p-4 shadow-sm md:mt-10 md:p-8">
      <div className="mb-6 flex flex-col gap-4 md:mb-8 md:flex-row md:items-end md:justify-between">
        <div>
          <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-red-600">
            {translateText('shortsSectionTag', 'Casos de exito')}
          </span>
          <h3 className="mt-3 text-[clamp(38px,5vw,58px)] font-black uppercase leading-[0.9] text-gray-900">
            {translateText('shortsSectionTitleLine1', 'De Rolling')}
            <br />
            {translateText('shortsSectionTitleLine2', 'al mundo')}
          </h3>
        </div>

        <Link
          href={YOUTUBE_CHANNEL_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-xs font-bold uppercase tracking-[0.12em] text-red-600 transition-colors hover:text-red-500"
        >
          {translateText('shortsSectionViewAll', 'Ver todos en YouTube')}
          <FiArrowUpRight className="ml-1" />
        </Link>
      </div>

      {(loading || isThumbnailLoading) && (
        <div className="mb-5 flex items-center justify-between rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-red-300 border-t-red-600" />
            <p className="text-sm font-semibold text-red-700">
              {loading
                ? translateText('shortsLoadingInitial', 'Cargando Shorts de YouTube...')
                : loadingThumbnailsText}
            </p>
          </div>
          <span className="text-xs font-semibold uppercase tracking-[0.1em] text-red-600">
            {translateText('shortsLoadingWait', 'Espere un momento')}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {loading &&
          Array.from({ length: 4 }, (_slot, skeletonIndex) => skeletonIndex).map((skeletonIndex) => (
            <article
              key={`short-skeleton-${skeletonIndex}`}
              className="relative aspect-[9/16] overflow-hidden rounded-xl border border-red-100 bg-red-50"
            >
              <div className="absolute inset-0 animate-pulse bg-gradient-to-b from-red-100/60 via-red-50/90 to-white" />
              <div className="absolute inset-x-4 top-4 h-4 rounded bg-red-200/80" />
              <div className="absolute inset-x-4 top-10 h-4 w-3/4 rounded bg-red-100/90" />
              <div className="absolute inset-x-4 bottom-4 flex items-center justify-between">
                <span className="h-5 w-20 rounded-full bg-white/80" />
                <span className="h-9 w-9 rounded-full bg-red-200/90" />
              </div>
            </article>
          ))}

        {!loading &&
          shorts.map((short) => (
            <article key={short.id} className="overflow-hidden rounded-xl border border-red-100 bg-white shadow-sm">
              <div className="relative aspect-[9/16] w-full">
                {!loadedShortIds[short.id] && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-red-50/80">
                    <div className="flex flex-col items-center gap-3">
                      <span className="h-8 w-8 animate-spin rounded-full border-2 border-red-200 border-t-red-600" />
                      <span className="text-xs font-semibold uppercase tracking-[0.12em] text-red-600">
                        {translateText('shortsLoadingSingle', 'Cargando short')}
                      </span>
                    </div>
                  </div>
                )}
                <Link
                  href={short.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block h-full w-full"
                  aria-label={translateText('shortsCardAriaLabel', 'Ver short en YouTube')}
                >
                  <Image
                    src={getThumbnailSrc(short.id, thumbnailVariantById[short.id] ?? 0)}
                    alt={translateText('shortsCardThumbnailAlt', 'Miniatura de YouTube Short')}
                    fill
                    sizes="(max-width: 640px) 100vw, (max-width: 1280px) 50vw, 25vw"
                    className="object-cover"
                    unoptimized
                    onLoad={() => markShortAsLoaded(short.id)}
                    onError={() => handleThumbnailError(short.id)}
                  />

                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-black/5 to-transparent" />
                  <div className="pointer-events-none absolute inset-x-3 bottom-3 flex items-center justify-between">
                    <span className="rounded-full bg-white/90 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-gray-900">
                      {translateText('shortsCardChip', 'YouTube Short')}
                    </span>
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-red-600 text-white shadow-md transition-transform duration-200 group-hover:scale-105">
                      ▶
                    </span>
                  </div>
                </Link>
              </div>
            </article>
          ))}
      </div>

      {!loading && shorts.length === 0 && (
        <p className="mt-4 text-sm text-gray-600">
          {translateText(
            'shortsSectionLoadError',
            'No pudimos cargar los Shorts en este momento. Podes verlos directamente en YouTube.'
          )}
        </p>
      )}
    </section>
  );
};

export default RollingCodeShortsSection;
